import { memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { classNames } from 'primereact/utils';
import { SearchablePopup } from './SearchablePopup';
import { useVirtualScroll } from './useVirtualScroll';
import { PROVIDER_TYPE_OPTIONS, ProviderType } from '@repo/atomic-components';
import { confirmDialog } from '@repo/atomic-components';
import { isPaginatorInfoDismissed } from '@/ui/components/PaginatorInfoDialog';
import { PAGINATOR_INFO_CONFIRM } from '@/constants/dialogs';

export interface CaseProvider {
  providerId: string;
  providerName: string;
  providerType: string;
}

interface ProviderSelectorPopupProps {
  caseProvidersList: CaseProvider[];
  onSelect: (providerId: string, providerName: string, providerType: string) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

export const ProviderSelectorPopup: React.FC<ProviderSelectorPopupProps> = memo(
  ({ caseProvidersList, onSelect, onClose, position }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [mode, setMode] = useState<'list' | 'create'>('list');
    const [selectedProviderType, setSelectedProviderType] = useState<string>('');
    const [focusedIndex, setFocusedIndex] = useState(0);
    const isKeyboardNavRef = useRef(false);

    const handleSelectWithPaginatorCheck = useCallback(
      async (providerId: string, providerName: string, providerType: string) => {
        if (providerType.toUpperCase() === 'PAGINATOR' && !isPaginatorInfoDismissed()) {
          onClose();
          const confirmed = await confirmDialog({ id: PAGINATOR_INFO_CONFIRM });
          if (!confirmed) return;
          onSelect(providerId, providerName, providerType);
          return;
        }
        onSelect(providerId, providerName, providerType);
        onClose();
      },
      [onSelect, onClose],
    );

    const filteredProviders = useMemo(() => {
      if (!searchQuery.trim()) return caseProvidersList;
      const query = searchQuery.toLowerCase();
      return caseProvidersList.filter((provider) => provider.providerName.toLowerCase().includes(query));
    }, [caseProvidersList, searchQuery]);

    // Virtual scrolling configuration
    const ITEM_HEIGHT = 44; // py-2.5 = 10px top + 10px bottom + 20px line-height + 4px spacing

    const { virtualItems, totalHeight, scrollRef, onScroll } = useVirtualScroll({
      itemCount: filteredProviders.length,
      itemHeight: ITEM_HEIGHT,
      containerHeight: 400, // maxHeight of SearchablePopup
      overscan: 5,
    });

    const existingTypes = useMemo(() => {
      if (!searchQuery.trim()) return [];
      const normalizedName = searchQuery.trim().toLowerCase();
      return caseProvidersList
        .filter((provider) => provider.providerName.toLowerCase() === normalizedName)
        .map((provider) => provider.providerType.toUpperCase() as ProviderType);
    }, [searchQuery, caseProvidersList]);

    const allTypesExist = PROVIDER_TYPE_OPTIONS.every((option) => existingTypes.includes(option.providerOptionType));

    const availableOptions = useMemo(() => {
      return PROVIDER_TYPE_OPTIONS.map((option) => ({
        ...option,
        disabled: existingTypes.includes(option.providerOptionType),
      }));
    }, [existingTypes]);

    const showCreateButton = searchQuery.trim() && !allTypesExist;

    // Auto-scroll focused item into view when keyboard navigating
    useEffect(() => {
      if (isKeyboardNavRef.current && scrollRef.current && focusedIndex >= 0) {
        const itemTop = focusedIndex * ITEM_HEIGHT;
        const itemBottom = itemTop + ITEM_HEIGHT;
        const scrollTop = scrollRef.current.scrollTop;
        const visibleHeight = scrollRef.current.clientHeight;
        const scrollBottom = scrollTop + visibleHeight;

        // Scroll up if item is above viewport
        if (itemTop < scrollTop) {
          scrollRef.current.scrollTop = itemTop;
        }
        // Scroll down if item is below viewport
        else if (itemBottom > scrollBottom) {
          scrollRef.current.scrollTop = itemBottom - visibleHeight;
        }

        isKeyboardNavRef.current = false;
      }
    }, [focusedIndex, ITEM_HEIGHT]);

    const handleCreateClick = () => {
      setMode('create');
      // Set GP as default if not disabled
      const gpOption = availableOptions.find((opt) => opt.providerOptionType === 'GP');
      setSelectedProviderType(gpOption && !gpOption.disabled ? 'GP' : '');
    };

    const handleBackClick = () => {
      setMode('list');
      setSelectedProviderType('');
    };

    const handleProviderTypeSelect = () => {
      if (selectedProviderType && searchQuery.trim()) {
        handleSelectWithPaginatorCheck('', searchQuery.trim(), selectedProviderType);
      }
    };

    const handleEnterPress = () => {
      if (focusedIndex === -1) {
        handleCreateClick();
      } else {
        const provider = filteredProviders[focusedIndex];
        if (provider) {
          handleSelectWithPaginatorCheck(provider.providerId, provider.providerName, provider.providerType);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Mark that keyboard navigation is happening
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        isKeyboardNavRef.current = true;
      }
    };

    // In create mode, render a simple non-searchable UI
    if (mode === 'create') {
      return (
        <SearchablePopup
          position={position}
          onClose={onClose}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by provider name"
          focusedIndex={0}
          onFocusedIndexChange={() => {}}
          itemCount={0}
          onEnterPress={() => {}}
          width="fit-content"
          autoFocusSearch={false}
        >
          {/* Create mode content */}
          <div className="border-fletchers-gray-110 border-b px-3 py-3">
            <button
              type="button"
              onClick={handleBackClick}
              className="flex cursor-pointer items-center gap-2 focus:outline-none"
            >
              <span className="pi pi-arrow-left text-fletchers-gray-200 text-[14px]"></span>
              <span className="text-fletchers-gray-200 text-[14px] font-semibold">Back to provider list</span>
            </button>
          </div>

          {/* Type selection */}
          <div className="flex flex-col">
            <div className="px-3 py-3">
              <label className="text-[14px] text-black">Select type for '{searchQuery.trim()}':</label>
            </div>
            <div className="flex flex-col gap-3 px-3 pb-4">
              {availableOptions.map((option) => (
                <label
                  key={option.providerOptionType}
                  className={classNames(
                    'flex cursor-pointer items-center gap-3',
                    option.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <input
                    type="radio"
                    name="providerType"
                    value={option.providerOptionType}
                    checked={selectedProviderType === option.providerOptionType}
                    onChange={() => {
                      if (!option.disabled) {
                        setSelectedProviderType(option.providerOptionType);
                      }
                    }}
                    disabled={option.disabled}
                    className="accent-fletchers-green-500 h-4 w-4 cursor-pointer"
                  />
                  <span className="text-[14px] leading-[20px] text-black">{option.providerOptionName}</span>
                </label>
              ))}
            </div>
            <div className="border-fletchers-gray-110 border-t"></div>
            <div className="flex justify-end px-3 py-3">
              <button
                type="button"
                onClick={handleProviderTypeSelect}
                disabled={!selectedProviderType}
                className={classNames(
                  'bg-fletchers-green-500 h-8 rounded px-4 text-[14px] font-semibold text-white transition-colors',
                  selectedProviderType
                    ? 'hover:bg-fletchers-green-600 cursor-pointer'
                    : 'cursor-not-allowed opacity-50',
                )}
              >
                Confirm
              </button>
            </div>
          </div>
        </SearchablePopup>
      );
    }

    // List mode
    return (
      <SearchablePopup
        position={position}
        onClose={onClose}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by provider name"
        focusedIndex={focusedIndex}
        onFocusedIndexChange={setFocusedIndex}
        itemCount={filteredProviders.length}
        minFocusIndex={showCreateButton ? -1 : 0}
        onEnterPress={handleEnterPress}
        onKeyDown={handleKeyDown}
        width="fit-content"
      >
        {/* Create button */}
        {showCreateButton && (
          <div className="border-fletchers-gray-110 border-b">
            <button
              type="button"
              onClick={handleCreateClick}
              onMouseEnter={() => setFocusedIndex(-1)}
              className={classNames(
                'w-full cursor-pointer px-3 py-2.5 text-left text-[14px] font-semibold focus:outline-none',
                focusedIndex === -1
                  ? 'bg-fletchers-green-50 text-fletchers-green-600'
                  : 'text-fletchers-green-500 hover:underline',
              )}
            >
              + Create '{searchQuery.trim()}'
            </button>
          </div>
        )}

        {/* Providers list with virtual scrolling */}
        {filteredProviders.length === 0 ? (
          <div className="flex items-center justify-center p-4" style={{ minWidth: '480px', maxWidth: '600px' }}>
            <span className="text-fletchers-gray-175 text-[14px]">No providers found</span>
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-b-lg"
            style={{ minWidth: '480px', maxWidth: '600px' }}
            onScroll={onScroll}
          >
            {/* Virtual scroll container */}
            <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
              {virtualItems.map((virtualItem) => {
                const provider = filteredProviders[virtualItem.index];
                return (
                  <div
                    key={provider.providerId}
                    className={classNames(
                      'absolute right-0 left-0 cursor-pointer px-3 py-2.5',
                      virtualItem.index === focusedIndex ? 'bg-fletchers-green-50' : 'hover:bg-fletchers-green-50',
                    )}
                    style={{ top: `${virtualItem.offsetTop}px`, height: `${ITEM_HEIGHT}px` }}
                    onClick={() => {
                      handleSelectWithPaginatorCheck(provider.providerId, provider.providerName, provider.providerType);
                    }}
                    onMouseEnter={() => setFocusedIndex(virtualItem.index)}
                    title={`${provider.providerName} (${provider.providerType})`}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <span className="overflow-hidden text-[14px] leading-[20px] overflow-ellipsis whitespace-nowrap text-black first-letter:uppercase">
                        {provider.providerName}
                      </span>
                      <span className="text-fletchers-gray-250 flex-shrink-0 text-[14px] leading-[20px] whitespace-nowrap">
                        {provider.providerType}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SearchablePopup>
    );
  },
);
